import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useImmer } from 'use-immer';

import Grid from '@mui/material/Grid2';
import { 
  NumberFeature,
  ModelBase,
  DisablableOption
} from "@models";
import { 
  Character,
  CharacterFeature,
  CreatureInstance,
  SummonInstance,
  CreatureAbility,
  MinionAbility,
  UpgradableNumberFeature,
  // EncounterLog,
  CreatureSpellAsAbility,
  Spell,
  TransformInstance,
  CharacterAction,
  SummonOption2,
  CharacterAbility,
  CharacterSpell,
  DamageTypeOverrides,
  CreatureGift,
  CharacterCondition,
} from "@dndModels";
import { CHARACTER_LEVELS } from "@dndModels/Constants";

import API from "@dndUtilities/system_api";
import CommonUtilities from "@utilities/common_utilities";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";
import DataUtilities from '@dndUtilities/data_utilities';

// import StringBox from "../../../../components/input/strings/StringBox";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import LoadingBox from "@components/input/LoadingBox";
import EditableLabel from "@components/input/strings/EditableLabel";

import CreatureInstanceSavingThrows from "@dndComponents/creature/CreatureInstanceSavingThrows";
import CreatureAbilityScores from "@dndComponents/creature/CreatureAbilityScores";

import CharacterResourceBoxesNew from "@dndComponents/character/misc/CharacterResourceBoxes";
import DisplayObjects from "@dndComponents/display/DisplayObjects";
import ConditionBar from "@dndComponents/other/ConditionBar";
import CreatureEffect from "@dndComponents/character/spells_and_abilities/CreatureEffect";
import CharacterActionInput from "@dndComponents/character/spells_and_abilities/CharacterActionInput";
import DisplayStringWithReplacements from "@dndComponents/display/DisplayStringWithReplacements";
import HPInput from "@dndComponents/other/HPInput";
import CreatureActionInput from "@dndComponents/creature/CreatureActionInput";
import CreatureInstanceSkills from "@dndComponents/creature/CreatureInstanceSkills";

interface Props { 
  character?: Character | null;
  creature_instance: CreatureInstance | SummonInstance | TransformInstance;
  trim?: boolean;
  true_id: string;
  view_only?: boolean;
  onChange?: (changed: CreatureInstance | SummonInstance | TransformInstance) => void;
  onSummon?: (spell: CharacterAbility | CharacterSpell | Spell | CreatureAbility | MinionAbility, level: number, selected_option: SummonOption2) => void;
}

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();
const char_util = DandDCharacterUtilities.getInstance();
const data_util = DataUtilities.getInstance();

export default function CreatureInstanceInput({ 
  character = null,
  creature_instance,
  trim = false,
  true_id,
  view_only = false,
  onChange,
  onSummon
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reload_resources, setReloadResources] = useState(false);
  const [selected_spell, setSelectedSpell] = useState<Spell | null>(null);
  const [prev_true_id, setTrueId] = useState(true_id);
  const [ping, setPing] = useState(false);
  const [expanded, setExpanded] = useImmer<{ [section: string]: boolean }>({});
  const mounted = useRef(false);
  const [creature_gifts, setCreatureGifts] = useState<CreatureGift[]>([]);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const recalculated = () => {
    setReloading(true);
  }

  const resourcesChanged = () => {
    console.log("resourcesChanged()"); // I think it's not working because it's using a clone of the object somewhere
    // setReloading(true);
    setReloadResources(true);
  }

  const loadCreature = useCallback(async () => {
    if (creature_instance instanceof CreatureInstance) {
      // debugger;
      setLoading(true);
      if (creature_instance.creature) {
        await api.loadObject(creature_instance.creature);
        await creature_instance.connectCreature(creature_instance.creature);
      }
      const res = await char_util.getCreatureGiftsForCreature(creature_instance);
      setCreatureGifts(res);
      setLoading(false);
    } 
  }, [creature_instance, setLoading, setCreatureGifts]);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      creature_instance.subscription_holder.subscribe("Recalculated", mounted, recalculated);
      creature_instance.subscription_holder.subscribe("Resources", mounted, resourcesChanged);
      loadCreature();
    }
  }, [fetched, creature_instance, setFetched, loadCreature]);

  useEffect(() => {
    if (reloading) {
      setReloading(false)
    }
  }, [reloading, setReloading]);

  useEffect(() => {
    if (reload_resources) {
      setReloadResources(false)
    }
  }, [reload_resources, setReloadResources]);

  useEffect(() => {
    if (true_id !== prev_true_id) {
      setTrueId(true_id);
      setReloading(true)
    }
  }, [true_id, prev_true_id, setTrueId, setReloading]);

  const disabled = api.user === null || !api.online || (character !== null && !(character.owner_id === api.user._id || (character.campaign !== null && character.campaign.owner_id === api.user._id)));
    
  const renderCRSelect = () => {
    if (!disabled && creature_instance instanceof CreatureInstance && 
      creature_instance.creature && 
      (creature_instance.creature.min_challenge_rating !== creature_instance.creature.challenge_rating || 
        creature_instance.creature.max_challenge_rating !== creature_instance.creature.challenge_rating)) {
      const options: DisablableOption[] = [];
      if (creature_instance.creature.name_table.length > 0) {
        for (let i = creature_instance.creature.min_challenge_rating; i <= creature_instance.creature.max_challenge_rating; ++i) {
          const name_for_cr = creature_instance.creature.name_table.find(o => o.level === i);
          if (name_for_cr) {
            options.push({ id: name_for_cr.name, text: name_for_cr.name, disabled: true });
          }
          options.push({ id: `${i}`, text: `${i}` });
        }
      } else {
        for (let i = creature_instance.creature.min_challenge_rating; i <= creature_instance.creature.max_challenge_rating; ++i) {
          options.push({ id: `${i}`, text: `${i}` });
        }
      }
      return (
        <Grid
          size={{
            xs: 12
          }}>
          <SelectStringBox
            name="Challenge Rating"
            options={options}
            value={`${creature_instance.challenge_rating}`}
            onChange={async (changed: string) => {
              const new_obj = creature_instance;
              if (new_obj instanceof CreatureInstance) {
                // debugger;
                await new_obj.change_challenge_rating(+changed);
                // const log = new EncounterLog();
                // log.field = "challenge_rating";
                // log.value = changed;
                if (onChange) {
                  onChange(new_obj);
                }
                setReloading(true);
              }
            }}
          />
        </Grid>
      );
    }
    return null;
  }
   
  const renderGiftsSelect = () => {
    if (!disabled && creature_instance instanceof CreatureInstance) {
      if (creature_gifts.length > 0) {
        const options = creature_gifts.map(o => {
          const opt: DisablableOption = {
            id: o._id,
            text: o.name
          };
          return opt;
        });
        return (
          <>
            <Grid
              size={{
                xs: 12
              }}>
              <SelectStringBox
                name="Creature Gifts"
                options={options}
                values={creature_instance.creature_gifts.map(o => o.gift_id)}
                onChange={ async (changed: string[]) => {
                  // debugger;
                  const new_obj = creature_instance;
                  if (new_obj.creature) {
                    const olds = new_obj.creature_gifts.filter(o => changed.includes(o.gift_id));
                    new_obj.creature_gifts = [];
                    await new_obj.copyCreature(new_obj.creature, new_obj.challenge_rating);
                    for (const gift_id of changed) {
                      const old = olds.find(o => o.gift_id === gift_id);
                      if (old && old.gift) {
                        new_obj.copyCreatureGift(old.gift, old.level);
                      } else {
                        const gift = creature_gifts.find(o => o._id === gift_id);
                        if (gift) {
                          new_obj.copyCreatureGift(gift, 1);
                        }
                      }
                    }
                  }
                  // const log = new EncounterLog();
                  // log.field = "creature_gifts";
                  // log.value = changed;
                  if (onChange) {
                    onChange(new_obj);
                  }
                  setReloading(true);
                }}
              />
            </Grid>
            { creature_instance.creature_gifts.filter(o => o.gift && o.gift.max_level > 1).map(cig => {
              if (cig.gift) {
                const gift = cig.gift;
                const level_options = CHARACTER_LEVELS.map(o => +o).filter(o => o <= gift.max_level).map(o => { return `${o}`; });
                return (
                  <Grid
                    key={cig.true_id}
                    size={{
                      xs: 12
                    }}>
                    <SelectStringBox
                      name={`Level for ${gift.name}`}
                      options={level_options}
                      value={`${cig.level}`}
                      disabled={disabled}
                      onChange={async (changed: string) => {
                        const new_obj = creature_instance;
                        const new_cig = new_obj.creature_gifts.find(o => o.true_id === cig.true_id);
                        if (new_cig) {
                          new_cig.level = +changed;
                          await new_obj.change_challenge_rating(new_obj.challenge_rating);
                        }
                        // const log = new EncounterLog();
                        // log.field = "creature_gifts";
                        // log.value = changed;
                        if (onChange) {
                          onChange(new_obj);
                        }
                        setReloading(true);
                      }}
                    />
                  </Grid>
                );
              }
              return null;
            })}
          </>
        );
      }
    }
    return null;
  }

  const renderAction = (feature: CharacterFeature) => {
    const floating_feature = feature.floating_feature;
    // debugger;
    // if (feature.name.includes("Slam")) {
    //   // console.log(floating_feature);
    //   debugger;
    // }
    // Need to get potence somehow.
    if (floating_feature) {
      if (floating_feature instanceof CreatureAbility || 
        // floating_feature instanceof CreatureSpellAsAbility ||
        floating_feature instanceof MinionAbility) {
        const dt_overrides: DamageTypeOverrides[] = [];
        if (floating_feature instanceof CreatureAbility && creature_instance instanceof CreatureInstance) {
          const dt_override_tables = creature_instance.creature_gifts.map(o => o.damage_type_overrides_table);
          dt_overrides.push(...dt_override_tables.flatMap(o => o.entries).filter(o => api.fitAbilityLimit(floating_feature, o.ability_limit)));
        }
        let description = floating_feature.description + "\r\n" + dt_overrides.map(o => { return `<b>${o.name}</b>\r\n ${o.description}`; }).join("\r\n");
        // if (floating_feature.name.includes("Breath")) {
        //   debugger;
        // }
        if (description.includes("{feature_source") || description.includes("{cfs")) {
          description = char_util.getStringWithReplacements(description, creature_instance);
        }
        return (
          <Grid key={feature.true_id} container spacing={0} direction="row">
            <Grid
              size={{
                xs: 12
              }}>
              <CreatureActionInput
                character={character}
                obj={creature_instance}
                char_feature={feature}
                action={floating_feature}
                dt_overrides={dt_overrides}
                disabled={disabled}
                trim={trim}
                onChange={() => {
                  if (onChange) {
                    onChange(creature_instance);
                  }
                  setPing(!ping);
                }}
                onClick={() => {
                  setExpanded(draft => {
                    if (draft[`action_${floating_feature._id}`]) {
                      delete draft[`action_${floating_feature._id}`];
                    } else {
                      draft[`action_${floating_feature._id}`] = true;
                    }
                  });
                }}
                onSummon={(selected_option: SummonOption2) => {
                  if (onSummon) {
                    if (floating_feature instanceof CreatureSpellAsAbility) {
                      if (floating_feature.spell) {
                        onSummon(floating_feature.spell, floating_feature.spell.level, selected_option);
                      }
                    } else {
                      onSummon(floating_feature, 0, selected_option);
                    }
                  }
                }}
              />
            </Grid>
            { expanded[`action_${floating_feature._id}`] &&
              <>
                <Grid
                  size={{
                    xs: 12
                  }}>
                  <DisplayStringWithReplacements
                    display_string={description}
                    cfs={creature_instance}
                    // log_it
                  /> 
                </Grid>
                { feature.floating_feature instanceof CreatureSpellAsAbility && feature.floating_feature.spell &&
                  <>
                    <Grid
                      style={{ fontWeight: "bold" }}
                      size={{
                        xs: 12
                      }}>Spell Description</Grid>
                    <Grid
                      size={{
                        xs: 12
                      }}>{ feature.floating_feature.spell.description }</Grid>
                  </>
                }
              </>
            }
          </Grid>
        );
      } else if (floating_feature instanceof UpgradableNumberFeature && floating_feature.feature_type === "Minion Extra Attacks") {
        let description = floating_feature.description;
        if (description.includes("{feature_source") || description.includes("{cfs")) {
          description = char_util.getStringWithReplacements(description, creature_instance);
        }
        return (
          <Grid key={feature.true_id} container spacing={0} direction="row">
            <Grid
              size={{
                xs: 4
              }}>
              <b>{ feature.name }</b>
            </Grid>
            <Grid
              size={{
                xs: 8
              }}>
              <DisplayStringWithReplacements
                display_string={description}
              /> 
            </Grid>
          </Grid>
        );
      } else {
        let description = floating_feature.description;
        if (description.includes("{feature_source") || description.includes("{cfs")) {
          description = char_util.getStringWithReplacements(description, creature_instance);
        }
        return (
          <Grid key={feature.true_id} container spacing={0} direction="row">
            { feature.feature_type === "Extra Attacks" ?
              <Grid
                size={{
                  xs: 12
                }}>
                <b>{ feature.name }</b> { `${(Math.floor((floating_feature as NumberFeature).num)) + 1} Attacks per Action` }
              </Grid>
            : 
              <>
                <Grid
                  size={{
                    xs: 12
                  }}>
                  <b className="clickable" onClick={() => {
                      setExpanded(draft => {
                        if (draft[`action_${floating_feature._id}`]) {
                          delete draft[`action_${floating_feature._id}`];
                        } else {
                          draft[`action_${floating_feature._id}`] = true;
                        }
                      });
                    }}>{ feature.name }</b>
                </Grid>
                { expanded[`action_${floating_feature._id}`] && feature.feature && 
                  <Grid
                    size={{
                      xs: 12
                    }}>
                    <DisplayStringWithReplacements
                      display_string={description}
                    /> 
                  </Grid>
                }
              </>
            }
          </Grid>
        );
      }
    }
    return null;
  }

  const renderCharacterAction = (action: CharacterAction) => {
    if (character) {
      return (
        <Grid key={action.true_id}>
          <CharacterActionInput
            char={character}
            char_interface={character}
            action={action}
            group={action.casting_time}
            // show_extra_effects={show_extra_effects}
            processing={false}
            shared={false}
            level={action.level}
            // onCharChange={() => {
            //   // onCharChange();
            //   // setReloading(true);
            // }}
            // onItemChange={onItemChange}
            // onTransform={() => {}}
            onOpen={() => {
              // setDetailsAction(action);
              // setDrawer("details");
            }}
          />
        </Grid>
      );
    }
    return null;
  }

  const renderResources = () => {
    const return_us: any[] = [];
    if (!reload_resources) {
      if (creature_instance.use_resources.length > 0) {
        return_us.push(
          <Grid
            key="resources"
            className='header'
            style={{ borderTop: "1px solid" }}
            size={{
              xs: 12
            }}>Resources</Grid>
        );
      }
      for (const r of creature_instance.use_resources) {
        const resource = data_util.resources.find(o => o._id === r.resource_id);
        if (resource) {
          return_us.push(
            <Grid
              key={r.resource_id}
              size={{
                xs: 12
              }}>
              <CharacterResourceBoxesNew 
                label={resource.use_name}
                // type={resource.use_name}
                show_type={false}
                used={r.used} 
                total={r.total} 
                true_total={r.total} 
                created={0} 
                shared={false}
                onChange={(used: number) => {
                  const r2 = creature_instance.use_resources.find(o => o.resource_id === r.resource_id);
                  if (r2) {
                    r2.used = used;
                    // const log = new EncounterLog();
                    // log.field = `resources`;
                    // log.value = creature_instance.use_resources;
                    if (onChange) {
                      onChange(creature_instance);
                    }
                    setPing(!ping);
                  }
                }}
              />
            </Grid>
          );
        }
      }
    }
    return return_us;
  }

  const renderSpells = () => {
    const levels = Object.keys(creature_instance.use_spells).map(o => +o).sort((a,b) => { return +a < +b ? -1 : 1; });
    let return_us: any[] = [];
    if (levels.length > 0) {
      return_us.push(
        <Grid
          key="spells_header"
          size={{
            xs: 12
          }}>
          <b className="clickable" onClick={() => { 
            setExpanded(draft => {
              if (draft.spells) {
                delete draft.spells;
              } else {
                draft.spells = true;
              }
            });
          }}>Spells</b>
        </Grid>
      );
    }
    if (expanded.spells) {
      if (creature_instance.spellcasting_description !== "") {
        return_us.push(
          <Grid
            key="spellcasting_desc"
            size={{
              xs: 12
            }}>
            <DisplayStringWithReplacements
              display_string={creature_instance.spellcasting_description} 
            />
          </Grid>
        );
      }
      const slot: any[] = [];
      const at_will: any[] = [];
      const x_day: any[] = [];
      for (const level of levels) {
        const spells_of_level = creature_instance.use_spells.filter(o => o.spell_level === level);
        if (level === 0 && spells_of_level.length > 0) {
          at_will.push(
            <Grid
              key={level}
              size={{
                xs: 12
              }}>
              Cantrips:&nbsp;
              <DisplayObjects type="spell" ids={spells_of_level.map(o => o.spell_id)} 
                onClick={(obj: ModelBase) => {
                  const new_spell = obj as Spell;
                  if (selected_spell && new_spell._id === selected_spell._id) {
                    setSelectedSpell(null);
                  } else {
                    setSelectedSpell(new_spell);
                  }
                }} 
              />
            </Grid>
          );
        } else {
          // debugger;
          let level_name = "";
          switch(level) {
            case 1: level_name = "1st"; break;
            case 2: level_name = "2nd"; break;
            case 3: level_name = "3rd"; break;
            default: level_name = `${level}th`; break;
          }
          const at_will_spells = spells_of_level.filter(o => o.cast_type === "At Will");
          if (at_will_spells.length > 0) {
            at_will.push(
              <Grid
                key={level}
                size={{
                  xs: 12
                }}>
                { level_name } level (At Will):&nbsp;
                <DisplayObjects type="spell" ids={at_will_spells.map(o => o.spell_id)} 
                  onClick={(obj: ModelBase) => {
                    const new_spell = obj as Spell;
                    if (selected_spell && new_spell._id === selected_spell._id) {
                      setSelectedSpell(null);
                    } else {
                      setSelectedSpell(new_spell);
                    }
                  }} 
                />
              </Grid>
            );
          }
          const slot_spells = spells_of_level.filter(o => o.cast_type === "Slot");
          const slot_count = creature_instance.use_slots2.find(o => o.resource_id === `Slot: ${level}`);
          if (slot_spells.length > 0 && slot_count && slot_count.total > 0) {
            slot.push(
              <Grid
                key={level}
                size={{
                  xs: 12
                }}>
                { level_name } level{ slot_count ? ` (${slot_count.total} Slots)` : "" }:&nbsp;
                <DisplayObjects type="spell" ids={slot_spells.map(o => o.spell_id)} 
                  onClick={(obj: ModelBase) => {
                    const new_spell = obj as Spell;
                    if (selected_spell && new_spell._id === selected_spell._id) {
                      setSelectedSpell(null);
                    } else {
                      setSelectedSpell(new_spell);
                    }
                  }} 
                />
              </Grid>
            );
            if (creature_instance instanceof CreatureInstance && !reload_resources) {
              slot.push(
                <Grid
                  key={`${level}_slots`}
                  size={{
                    xs: 12
                  }}>
                  <CharacterResourceBoxesNew 
                    used={creature_instance.used_slots[level] || 0}
                    total={slot_count.total}
                    true_total={slot_count.total}
                    created={0}
                    show_type={false}
                    shared={false}
                    onChange={(used: number) => {
                      const new_obj = creature_instance as CreatureInstance;
                      new_obj.used_slots[level] = used;
                      // const log = new EncounterLog();
                      // log.field = "used_slots";
                      // log.value = new_obj.used_slots;
                      
                      if (onChange) {
                        onChange(new_obj);
                      }
                      setPing(!ping);
                    }}
                  />
                </Grid>
              );
            }
          }
          const x_day_spells = spells_of_level.filter(o => o.cast_type === "X/Day");
          if (x_day_spells.length > 0) {
            // I should probably change this to happen on copy/connectCreature
            const spellcasting_entry = creature_instance.creature ? creature_instance.creature.spellcasting_table.getEntry(creature_instance.challenge_rating) : undefined;
            if (spellcasting_entry) {
              const spell_ids_by_x: { [x: number]: string[] } = {};
              for (const x_day_spell of x_day_spells) {
                const x = spellcasting_entry.spells.filter(o => o.spell_id === x_day_spell.spell_id)[0].x;
                if (x) {
                  if (!spell_ids_by_x[x]) {
                    spell_ids_by_x[x] = [];
                  }
                  spell_ids_by_x[x].push(x_day_spell.spell_id);
                }
              }
              if (!reload_resources) {
                for (const ex of Object.keys(spell_ids_by_x).map(o => +o)) {
                  const spell_ids = spell_ids_by_x[ex];
                  x_day.push(
                    <Grid
                      key={`${level}_${ex}`}
                      size={{
                        xs: 12
                      }}>
                      { level_name } level ({ ex }/Day{ spell_ids.length > 1 ? " each" : "" }):&nbsp;
                      <DisplayObjects type="spell" ids={spell_ids} 
                        onClick={(obj: ModelBase) => {
                          const new_spell = obj as Spell;
                          if (selected_spell && new_spell._id === selected_spell._id) {
                            setSelectedSpell(null);
                          } else {
                            setSelectedSpell(new_spell);
                          }
                        }} 
                      />
                    </Grid>
                  );
                }
              }
            }
          }
        }
      }
      return_us = [...return_us,...at_will,...x_day,...slot];
      
      if (selected_spell) {
        return_us.push(
          <Grid
            key="selected_spell"
            container
            spacing={0}
            direction="row"
            size={{
              xs: 12
            }}>
            <Grid
              style={{ fontWeight: "bold" }}
              size={{
                xs: 12
              }}>
              { selected_spell.use_name }
            </Grid>
            <Grid
              size={{
                xs: 12
              }}>
              <DisplayStringWithReplacements
                display_string={selected_spell.description} 
              />
            </Grid>
            { creature_instance instanceof CreatureInstance && selected_spell.effects.map((effect, key) => {
              return (
                <Grid
                  key={key}
                  size={{
                    xs: 12
                  }}>
                  <CreatureEffect 
                    creature_instance={creature_instance} 
                    effect={effect} 
                    spell={selected_spell}
                    processing={false} 
                    disabled={disabled} 
                    shared={false} 
                    level={selected_spell.level} 
                    wide={false} 
                    casting_time={''} 
                    onChange={(change_types: string[]) => {
                      throw new Error('Function not implemented.');
                    }} 
                    onCostChange={(costs: { [resource: string]: number; }) => {
                      throw new Error('Function not implemented.');
                    }} 
                    onTransform={() => {
                      throw new Error('Function not implemented.');
                    }} 
                    onSummon={(selected_option: SummonOption2) => {
                      if (onSummon) {
                        onSummon(selected_spell, selected_spell.level, selected_option);
                      }
                    }}
                  />
                </Grid>
              );
            })}
          </Grid>
        );
      }
    }
    return return_us;
  }

  const renderCreatureInstanceConditions = () => {
    const active_conditions = creature_instance.condition_hash;
    return (
      <div
        style={{
          display: "flex",
          minHeight: "76px",
          overflowX: "auto" 
        }}>
        <ConditionBar
          active_conditions={active_conditions}
          starred={creature_instance.favorite_conditions}
          immune={creature_instance.condition_immunities}
          // extra_ids={["Any"]}
          disabled={disabled}
          onChange={(condition_id: string, level: number) => {
            const new_obj = creature_instance;
            if (level === 0) {
              // delete new_obj.condition_hash[condition_id];
              new_obj.conditions = new_obj.conditions.filter(o => o.source_id !== condition_id);
            } else {
              let char_cond = new_obj.conditions.find(o => o.source_id === condition_id);
              if (!char_cond) {
                const cond = data_util.conditions.find(o => o._id === condition_id);
                if (cond) {
                  char_cond = new CharacterCondition();
                  char_cond.source_id = condition_id;
                  // char_cond.copy_source({ char: new_obj, feature_source: cond });
                  new_obj.conditions.push(char_cond);
                }
              }
              if (char_cond) {
                char_cond.level = level;
              }
              // new_obj.condition_hash[condition_id] = { level, source_true_id: "" };
            }
            // if (level === 0) {
            //   delete new_obj.condition_hash[condition_id];
            // } else {
            //   new_obj.condition_hash[condition_id] = { level, source_true_id: "" };
            // }
            // const log = new EncounterLog();
            // log.field = "condition_hash";
            // log.value = new_obj.condition_hash;
            if (onChange) {
              onChange(new_obj);
            }
            setPing(!ping);
          }}
          onStar={(id: string) => {
            const new_obj = creature_instance;
            if (new_obj.favorite_conditions.includes(id)) {
              new_obj.favorite_conditions = new_obj.favorite_conditions.filter(o => o !== id);
            } else {
              new_obj.favorite_conditions.push(id);
            }
            // const log = new EncounterLog();
            // log.field = "favorite_conditions";
            // log.value = new_obj.favorite_conditions;
            if (onChange) {
              onChange(new_obj);
            }
            setPing(!ping);
          }}
          onImmune={(id: string) => {
            const new_obj = creature_instance;
            if (new_obj.condition_immunities.includes(id)) {
              new_obj.condition_immunities = new_obj.condition_immunities.filter(o => o !== id);
            } else {
              new_obj.condition_immunities.push(id);
            }
            // const log = new EncounterLog();
            // log.field = "condition_immunities";
            // log.value = new_obj.condition_immunities;
            if (onChange) {
              onChange(new_obj);
            }
            setPing(!ping);
          }}
        />
      </div>
    );
  }

  const renderExtraStringsAndNumbers = () => {
    if (creature_instance.creature) {
      const string_keys = Object.keys(creature_instance.creature.string_hash);
      const number_keys = Object.keys(creature_instance.creature.upgradable_hash);
      const return_us: any[] = [];
      for (const key of string_keys) {
        return_us.push(
          <Grid
            key={`string_${key}`}
            size={{
              xs: 12
            }}>
            <span className="header">{ key }</span>&nbsp;{ creature_instance.creature.string_hash[key] }
          </Grid>
        );
      }
      for (const key of number_keys) {
        const upg = creature_instance.creature.upgradable_hash[key];
        const value = char_util.value(upg, { obj: creature_instance });
        return_us.push(
          <Grid
            key={`number_${key}`}
            size={{
              xs: 12
            }}>
            <span className="header">{ key }</span>&nbsp;{ value }
          </Grid>
        );
      }
      return return_us;
    }
    return null;
  }

  // console.log(creature_instance);
  if (reloading || !fetched || loading) {
    return (<LoadingBox />);
  } else if (trim) {
    // console.log(creature_instance);
    return (
      <Grid container spacing={0} direction="row" 
        style={{ 
          lineHeight: "1.5",
        }}>
        <Grid
          size={{
            xs: 4
          }}>
          <EditableLabel 
            name="Name"
            value={creature_instance.name}
            disabled={disabled || view_only}
            flash_on_empty
            onChange={(changed: string) => {
              const new_obj = creature_instance;
              new_obj.name = changed;
              // const log = new EncounterLog();
              // log.field = "name";
              // log.value = changed;
              if (onChange) {
                onChange(new_obj);
              }
              setPing(!ping);
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 2
          }}>
          <b>AC</b> { creature_instance.armor_class }
        </Grid>
        <Grid
          size={{
            xs: 2
          }}>
          <b>CR</b> { creature_instance.use_challenge_rating }{ ` (${ common_util.cr_to_xp_map[creature_instance.use_challenge_rating] } XP)` }
        </Grid>
        <Grid
          size={{
            xs: 2
          }}>
          <b>Prof. Bonus</b> { creature_instance.proficiency_modifier }
        </Grid>
        <Grid
          size={{
            xs: 2
          }}>
          <HPInput
            char={creature_instance}
            // hp={creature_instance.hp}
            disabled={ disabled }
            view_only={view_only}
          />
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          { creature_instance.use_size } { creature_instance.creature_type }{ creature_instance.subtypes.length > 0 && ` (${creature_instance.subtypes[0]})` }{ creature_instance.alignment !== "" && `, ${creature_instance.alignment}` }
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <b>Speed</b> { creature_instance.speed_string }
        </Grid>
        { !view_only &&
          <Grid
            size={{
              xs: 12
            }}>
            <b className="clickable" onClick={() => { 
              if (!view_only) {
                setExpanded(draft => {
                  if (draft.condition) {
                    delete draft.condition;
                  } else {
                    draft.condition = true;
                  }
                });
              }
            }}>Conditions</b> { expanded.condition ? renderCreatureInstanceConditions() : <DisplayObjects type="condition" ids={Object.keys(creature_instance.condition_hash) } /> }
          </Grid>
        }
        <Grid
          container
          spacing={0}
          direction="row"
          style={{ borderTop: "1px solid" }}
          size={{
            xs: 12
          }}>
          <b className={ view_only ? "" : "clickable" } onClick={() => { 
            if (!view_only) {
              setExpanded(draft => {
                if (draft.stats) {
                  delete draft.stats;
                } else {
                  draft.stats = true;
                }
              });
            }
          }}>Ability Scores</b>
          { expanded.stats && 
            <CreatureAbilityScores 
              obj={creature_instance}
              disabled={ disabled || view_only }
              trim
              onChange={() => {

              }}
            />
          }
        </Grid>
        <Grid
          style={{ borderTop: "1px solid" }}
          size={{
            xs: 12
          }}>
          <b className={ view_only ? "" : "clickable" } onClick={() => { 
            if (!view_only) {
              setExpanded(draft => {
                if (draft.saves) {
                  delete draft.saves;
                } else {
                  draft.saves = true;
                }
              });
            }
          }}>Saving Throws</b>
          { expanded.saves && 
            <CreatureInstanceSavingThrows 
              obj={creature_instance}
              disabled={ disabled }
              trim
              onChange={() => {

              }}
            />
          }
        </Grid>
        { creature_instance.skills_string.length > 0 &&
          <>
            <Grid
              className={ view_only ? "" : "clickable" }
              onClick={() => { 
                if (!view_only) {
                  setExpanded(draft => {
                    if (draft.skills) {
                      delete draft.skills;
                    } else if (creature_instance instanceof CreatureInstance) {
                      draft.skills = true;
                    }
                  });
                }
              }}
              size={{
                xs: 12
              }}>
              <b>Skills</b> { creature_instance.skills_string }
            </Grid>
            { expanded.skills && creature_instance instanceof CreatureInstance && 
              <Grid
                size={{
                  xs: 12
                }}>
                <CreatureInstanceSkills 
                  obj={creature_instance}
                  disabled={ disabled || view_only }
                  trim
                  onChange={() => {

                  }}
                />
              </Grid>
            }
          </>
        }
        { creature_instance.tools_string.length > 0 &&
          <Grid
            size={{
              xs: 12
            }}>
            <b className={ view_only ? "" : "clickable" } onClick={() => { 
              if (!view_only) {
                setExpanded(draft => {
                  if (draft.tools) {
                    delete draft.tools;
                  } else {
                    draft.tools = true;
                  }
                });
              }
            }}>Tools</b> { expanded.tools ? creature_instance.tools_string : "" }
          </Grid>
        }
        { creature_instance.immunities_string.length > 0 && 
          <Grid
            size={{
              xs: 12
            }}>
            <b>Damage Immunities</b> { creature_instance.immunities_string }
          </Grid>
        }
        { creature_instance.resistances_string.length > 0 && 
          <Grid
            size={{
              xs: 12
            }}>
            <b>Damage Resistances</b> { creature_instance.resistances_string }
          </Grid>
        }
        { creature_instance.vulnerabilities_string.length > 0 && 
          <Grid
            size={{
              xs: 12
            }}>
            <b>Damage Vulnerabilities</b> { creature_instance.vulnerabilities_string }
          </Grid>
        }
        { creature_instance.condition_immunities.length > 0 && 
          <Grid
            size={{
              xs: 12
            }}>
            <b>Condition Immunities</b> <DisplayObjects type="condition" ids={ creature_instance.condition_immunities } />
          </Grid>
        }
        <Grid
          size={{
            xs: 12
          }}>
          <b>Senses</b> { creature_instance.senses_string }
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <b>Languages</b> { creature_instance.languages }
        </Grid>
        { renderExtraStringsAndNumbers() }
        { creature_instance instanceof CreatureInstance && creature_instance.legendary_resistances > 0 && !reload_resources &&
          <Grid
            style={{
              fontSize: "20px",
              fontWeight: "bold",
              lineHeight: "1.1",
              // marginLeft: "-20px"
            }}
            size={{
              xs: 12
            }}>
            <CharacterResourceBoxesNew 
              used={creature_instance.used_legendary_resistances}
              total={creature_instance.legendary_resistances}
              true_total={creature_instance.legendary_resistances}
              created={0}
              type={"Legendary Resistances"}
              show_type={false}
              label='Legendary Resistances'
              marginLeft={0}
              shared={false}
              disabled={view_only}
              onChange={(used: number) => {
                if (!view_only) {
                  const new_obj = creature_instance as CreatureInstance;
                  new_obj.used_legendary_resistances = used;
                  new_obj.subscription_holder.resourcesChanged();
                  // const log = new EncounterLog();
                  // log.field = "used_legendary_resistances";
                  // log.value = used;
                  if (onChange) {
                    onChange(new_obj);
                  }
                  setPing(!ping);
                }
              }}
            />
          </Grid>
        }
        { renderResources() }
        { renderSpells() }
        { creature_instance.special_abilities.length > 0 &&
          <Grid
            style={{
              fontSize: "20px",
              fontWeight: "bold"
            }}
            size={{
              xs: 12
            }}>
            Traits
          </Grid>
        }
        { creature_instance.special_abilities.length > 0 &&
          <Grid
            container
            spacing={0}
            direction="column"
            style={{ borderTop: "1px solid" }}
            size={{
              xs: 12
            }}>
            { creature_instance.special_abilities.map(feature => {
              return renderAction(feature);
            })}
          </Grid>
        }
        { creature_instance.actions.length > 0 &&
          <Grid
            style={{
              fontSize: "20px",
              fontWeight: "bold"
            }}
            size={{
              xs: 12
            }}>
            Actions
          </Grid>
        }
        { creature_instance.actions.length > 0 &&
          <Grid
            style={{ borderTop: "1px solid" }}
            size={{
              xs: 12
            }}>
            { creature_instance.actions.map(feature => {
              // console.log(feature);
              if (feature instanceof CharacterAction) {
                return renderCharacterAction(feature);
              } else {
                return renderAction(feature);
              }
            })}
          </Grid>
        }
        { creature_instance instanceof CreatureInstance && creature_instance.creature && creature_instance.legendary_actions.length > 0 && !reload_resources &&
          <Grid
            style={{
              fontSize: "20px",
              fontWeight: "bold",
              lineHeight: "1.1"
            }}
            size={{
              xs: 12
            }}>
            <CharacterResourceBoxesNew 
              used={creature_instance.used_legendary_actions}
              total={creature_instance.legendary_actions_per_round}
              true_total={creature_instance.legendary_actions_per_round}
              created={0}
              type={"Legendary Actions"}
              show_type={false}
              label="Legendary Actions"
              marginLeft={0}
              shared={false}
              disabled={view_only}
              onChange={(used: number) => {
                if (!view_only) {
                  const new_obj = creature_instance as CreatureInstance;
                  new_obj.used_legendary_actions = used;
                  new_obj.subscription_holder.resourcesChanged();
                  // const log = new EncounterLog();
                  // log.field = "used_legendary_actions";
                  // log.value = used;
                  if (onChange) {
                    onChange(new_obj);
                  }
                  setPing(!ping);
                }
              }}
            />
          </Grid>
        }
        { creature_instance.legendary_actions.length > 0 &&
          <Grid
            style={{ borderTop: "1px solid" }}
            size={{
              xs: 12
            }}>
            { creature_instance.legendary_actions.map(feature => {
              // console.log(feature);
              return renderAction(feature);
            })}
          </Grid>
        }
        { creature_instance instanceof CreatureInstance && creature_instance.lair_actions.length > 0 &&
          <Grid
            style={{
              fontSize: "20px",
              fontWeight: "bold"
            }}
            size={{
              xs: 12
            }}>
            Lair Actions
          </Grid>
        }
        { creature_instance instanceof CreatureInstance && creature_instance.lair_actions.length > 0 &&
          <Grid
            style={{ borderTop: "1px solid" }}
            size={{
              xs: 12
            }}>
            { creature_instance.lair_actions.map(feature => {
              return renderAction(feature);
            })}
          </Grid>
        }
        { creature_instance.description.length > 0 &&
          <Grid
            style={{
              fontSize: "20px",
              fontWeight: "bold"
            }}
            className={ view_only ? "" : "clickable" }
            onClick={() => { 
              if (!view_only) {
                setExpanded(draft => {
                  if (draft.description) {
                    delete draft.description;
                  } else {
                    draft.description = true;
                  }
                });
              }
            }}
            size={{
              xs: 12
            }}>
            Description
          </Grid>
        }
        { expanded.description && creature_instance.description.length > 0 &&
          <Grid
            style={{ borderTop: "1px solid" }}
            size={{
              xs: 12
            }}>
            <DisplayStringWithReplacements
              display_string={creature_instance.description}
            /> 
          </Grid>
        }
      </Grid>
    );
  } else {
    // console.log(creature_instance);
    // console.log(creature_instance.actions);
    // console.log(creature_instance.abilities);
    return (
      <Grid container spacing={0} direction="row" 
        style={{ 
          lineHeight: "1.5",
        }}>
        <Grid
          style={{
            fontSize: "25px",
            fontWeight: "bold", 
            margin: "4px"
          }}
          size={{
            xs: 12
          }}>
          <EditableLabel 
            name="Name"
            value={creature_instance.name}
            disabled={disabled || view_only}
            flash_on_empty
            onChange={(changed: string) => {
              const new_obj = creature_instance;
              new_obj.name = changed;
              if (onChange) {
                onChange(new_obj);
              }
              setPing(!ping);
            }}
          />
        </Grid>
        { !view_only && renderCRSelect() }
        { !view_only && renderGiftsSelect() }
        { creature_instance.image_url !== "" &&
          <Grid
            className="centered"
            size={{
              xs: 12
            }}>
            <img src={ creature_instance.image_url } alt={creature_instance.name}
              style={{
                maxWidth: "200px",
                maxHeight: "200px",
                border: "1px solid",
              }}
            />
          </Grid>
        }
        <Grid
          style={{ color: "black" }}
          size={{
            xs: 12
          }}>
          { creature_instance.use_size } { creature_instance.creature_type }{ creature_instance.subtypes.length > 0 && ` (${creature_instance.subtypes[0]})` }{ creature_instance.alignment !== "" && `, ${creature_instance.alignment}` }
        </Grid>
        <Grid
          style={{ borderTop: "1px solid" }}
          size={{
            xs: 12
          }}>
          <b>Armor Class</b> { creature_instance.armor_class }
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <HPInput
            char={creature_instance}
            // hp={creature_instance.hp}
            disabled={ disabled }
            view_only={view_only}
          />
          {/* { renderHPStuff() } */}
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <b>Speed</b> { creature_instance.speed_string }
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          { !view_only && renderCreatureInstanceConditions() }
        </Grid>
        <Grid
          container
          spacing={0}
          direction="row"
          style={{ borderTop: "1px solid" }}
          size={{
            xs: 12
          }}>
          <CreatureAbilityScores 
            disabled={ disabled || view_only }
            obj={creature_instance}
            onChange={() => {

            }}
          />
        </Grid>
        <Grid
          style={{ borderTop: "1px solid" }}
          size={{
            xs: 12
          }}>
          <CreatureInstanceSavingThrows 
            disabled={ disabled || view_only }
            obj={creature_instance}
            onChange={() => {

            }}
          />
        </Grid>
        { creature_instance.skills_string.length > 0 &&
          <Grid
            size={{
              xs: 12
            }}>
            <b>Skills</b> { creature_instance.skills_string }
          </Grid>
        }
        { creature_instance.tools_string.length > 0 &&
          <Grid
            size={{
              xs: 12
            }}>
            <b>Tools</b> { creature_instance.tools_string }
          </Grid>
        }
        { creature_instance.immunities_string.length > 0 && 
          <Grid
            size={{
              xs: 12
            }}>
            <b>Damage Immunities</b> { creature_instance.immunities_string }
          </Grid>
        }
        { creature_instance.resistances_string.length > 0 && 
          <Grid
            size={{
              xs: 12
            }}>
            <b>Damage Resistances</b> { creature_instance.resistances_string }
          </Grid>
        }
        { creature_instance.vulnerabilities_string.length > 0 && 
          <Grid
            size={{
              xs: 12
            }}>
            <b>Damage Vulnerabilities</b> { creature_instance.vulnerabilities_string }
          </Grid>
        }
        { creature_instance.condition_immunities.length > 0 && 
          <Grid
            size={{
              xs: 12
            }}>
            <b>Condition Immunities</b> <DisplayObjects type="condition" ids={ creature_instance.condition_immunities } />
          </Grid>
        }
        <Grid
          size={{
            xs: 12
          }}>
          <b>Senses</b> { creature_instance.senses_string }
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <b>Languages</b> { creature_instance.languages }
        </Grid>
        <Grid
          size={{
            xs: 6
          }}>
          <b>Challenge</b> { creature_instance.use_challenge_rating }{ ` (${ common_util.cr_to_xp_map[creature_instance.use_challenge_rating] } XP)` }
        </Grid>
        <Grid
          size={{
            xs: 6
          }}>
          <b>Prof. Bonus</b> { creature_instance.proficiency_modifier }
        </Grid>
        { renderExtraStringsAndNumbers() }
        { creature_instance instanceof CreatureInstance && creature_instance.legendary_resistances > 0 && !reload_resources &&
          <Grid
            style={{
              fontSize: "20px",
              fontWeight: "bold"
            }}
            size={{
              xs: 12
            }}>
            Legendary Resistances 
            <CharacterResourceBoxesNew 
              used={creature_instance.used_legendary_resistances}
              total={creature_instance.legendary_resistances}
              true_total={creature_instance.legendary_resistances}
              created={0}
              type={"Legendary Resistances"}
              show_type={false}
              shared={false}
              disabled={view_only}
              onChange={(used: number) => {
                if (!view_only) {
                  const new_obj = creature_instance as CreatureInstance;
                  new_obj.used_legendary_resistances = used;
                  new_obj.subscription_holder.resourcesChanged();
                  // const log = new EncounterLog();
                  // log.field = "used_legendary_resistances";
                  // log.value = used;
                  if (onChange) {
                    onChange(new_obj);
                  }
                  setPing(!ping);
                }
              }}
            />
          </Grid>
        }
        { renderResources() }
        { renderSpells() }
        <Grid
          container
          spacing={0}
          direction="column"
          style={{ borderTop: "1px solid" }}
          size={{
            xs: 12
          }}>
          { creature_instance.special_abilities.map(feature => {
            return renderAction(feature);
          })}
        </Grid>
        { creature_instance.actions.length > 0 &&
          <Grid
            style={{
              fontSize: "20px",
              fontWeight: "bold"
            }}
            size={{
              xs: 12
            }}>
            Actions
          </Grid>
        }
        { creature_instance.actions.length > 0 &&
          <Grid
            style={{ borderTop: "1px solid" }}
            size={{
              xs: 12
            }}>
            { creature_instance.actions.map(feature => {
              // console.log(feature);
              if (feature instanceof CharacterAction) {
                return renderCharacterAction(feature);
              } else {
                return renderAction(feature);
              }
            })}
          </Grid>
        }
        { creature_instance instanceof CreatureInstance && creature_instance.creature && creature_instance.legendary_actions.length > 0 && !reload_resources &&
          <Grid
            style={{
              fontSize: "20px",
              fontWeight: "bold"
            }}
            size={{
              xs: 12
            }}>
            Legendary Actions 
            <CharacterResourceBoxesNew 
              used={creature_instance.used_legendary_actions}
              total={creature_instance.legendary_actions_per_round}
              true_total={creature_instance.legendary_actions_per_round}
              created={0}
              type={"Legendary Actions"}
              show_type={false}
              shared={false}
              disabled={view_only}
              onChange={(used: number) => {
                if (!view_only) {
                  const new_obj = creature_instance as CreatureInstance;
                  new_obj.used_legendary_actions = used;
                  new_obj.subscription_holder.resourcesChanged();
                  // const log = new EncounterLog();
                  // log.field = "used_legendary_actions";
                  // log.value = used;
                  if (onChange) {
                    onChange(new_obj);
                  }
                  setPing(!ping);
                }
              }}
            />
          </Grid>
        }
        { creature_instance.legendary_actions.length > 0 &&
          <Grid
            style={{ borderTop: "1px solid" }}
            size={{
              xs: 12
            }}>
            { creature_instance.legendary_actions.map(feature => {
              return renderAction(feature);
            })}
          </Grid>
        }
        { creature_instance instanceof CreatureInstance && creature_instance.lair_actions.length > 0 &&
          <Grid
            style={{
              fontSize: "20px",
              fontWeight: "bold"
            }}
            size={{
              xs: 12
            }}>
            Lair Actions
          </Grid>
        }
        { creature_instance instanceof CreatureInstance && creature_instance.lair_actions.length > 0 &&
          <Grid
            style={{ borderTop: "1px solid" }}
            size={{
              xs: 12
            }}>
            { creature_instance.lair_actions.map(feature => {
              return renderAction(feature);
            })}
          </Grid>
        }
        { creature_instance.description.length > 0 &&
          <Grid
            style={{
              fontSize: "20px",
              fontWeight: "bold"
            }}
            size={{
              xs: 12
            }}>
            Description
          </Grid>
        }
        { creature_instance.description.length > 0 &&
          <Grid
            style={{ borderTop: "1px solid" }}
            size={{
              xs: 12
            }}>
            <DisplayStringWithReplacements
              display_string={creature_instance.description}
            /> 
          </Grid>
        }
      </Grid>
    ); 
  }
}
